// ---------------------------------------------------------------------------
// FATURA DE CARTÃO — convenção do app (leia antes de mexer)
//
// A fatura leva o nome do mês em que VENCE. "Fatura de julho" = a que você paga
// em julho. Não é o mês em que ela fecha.
//
// Isso não é gosto: é como os dados existem. Todo lançamento de cartão importado
// do LunchMoney está datado no DIA DO VENCIMENTO (BAC: dia 15; Porto: dia 10;
// Itaú: dia 5), não na data da compra. Logo o mês da data JÁ É o mês da fatura,
// e `statement_month` nulo (todo o histórico) continua caindo no balde certo.
//
// O fechamento é que anda para trás:
//     BAC — fecha 24, vence 15
//     fatura de JULHO  →  fecha 24/06  ·  vence 15/07
//     compra em 14/07  →  fecha 24/07  →  vence 15/08  →  fatura de AGOSTO
// ---------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, '0')

// Um dia do mês (1..31) em (year, month0), encurtado se o mês for curto.
function dayInMonth(day: number, year: number, month0: number) {
  const last = new Date(year, month0 + 1, 0).getDate()
  return Math.min(day, last)
}

// Data de vencimento (dd/mm/aaaa) da fatura de um mês. Como a fatura é nomeada
// pelo mês do vencimento, é simplesmente o dueDay desse mês.
export function dueDateForMonth(
  dueDay: number | null | undefined,
  year: number,
  month0: number, // 0-based (0 = janeiro)
): string | null {
  if (!dueDay) return null
  return `${pad(dayInMonth(dueDay, year, month0))}/${pad(month0 + 1)}/${year}`
}

// Mesma data em ISO (yyyy-mm-dd) — útil para ordenação.
export function dueISOForMonth(
  dueDay: number | null | undefined,
  year: number,
  month0: number,
): string | null {
  if (!dueDay) return null
  return `${year}-${pad(month0 + 1)}-${pad(dayInMonth(dueDay, year, month0))}`
}

// Em que mês FECHA a fatura que vence em (year, month0)?
//   closingDay >= dueDay → fecha no mês ANTERIOR (BAC: fecha 24/06, vence 15/07)
//   closingDay <  dueDay → fecha no MESMO mês   (fecha 05/07, vence 20/07)
function closeMonthOfStatement(
  dueDay: number | null | undefined,
  closingDay: number | null | undefined,
  year: number,
  month0: number,
): { year: number; month0: number } | null {
  if (!closingDay) return null
  const shift = dueDay && closingDay >= dueDay ? -1 : 0
  const d = new Date(year, month0 + shift, 1)
  return { year: d.getFullYear(), month0: d.getMonth() }
}

// Data de FECHAMENTO (dd/mm/aaaa) da fatura que vence em (year, month0).
// null = cartão sem dia de fechamento cadastrado.
export function closeDateForStatement(
  dueDay: number | null | undefined,
  closingDay: number | null | undefined,
  year: number,
  month0: number,
): string | null {
  const c = closeMonthOfStatement(dueDay, closingDay, year, month0)
  return c && closingDay ? `${pad(dayInMonth(closingDay, c.year, c.month0))}/${pad(c.month0 + 1)}/${c.year}` : null
}

// Mês de fatura em ISO — sempre dia 01 (formato de transactions.statement_month).
export function statementMonthISO(year: number, month0: number): string {
  return `${year}-${pad(month0 + 1)}-01`
}

// SUGESTÃO da fatura de uma compra: em qual fatura (mês de VENCIMENTO) ela cai.
//
//   1) acha o FECHAMENTO que vai capturar a compra: o primeiro closingDay >= data
//   2) traduz esse fechamento para o mês em que a fatura correspondente VENCE
//
// Sem dia de fechamento cadastrado, cai no mês da própria data — que é como o app
// se comportava antes de `statement_month` existir, e é o que mantém o histórico
// importado (datado no vencimento) no balde certo.
export function suggestStatementMonth(
  dateISO: string,
  closingDay: number | null | undefined,
  dueDay?: number | null,
): string {
  const [y, m, d] = dateISO.split('-').map(Number)
  if (!y || !m || !d) return dateISO
  if (!closingDay) return `${y}-${pad(m)}-01`

  // mês em que a compra é capturada pelo fechamento (0-based, pode virar o ano)
  const closeShift = d <= closingDay ? 0 : 1
  // do fechamento para o vencimento: se o fechamento é >= o vencimento, a fatura
  // que ele fecha só vence no mês seguinte (é a inversa de closeMonthOfStatement)
  const dueShift = dueDay && closingDay >= dueDay ? 1 : 0
  const due = new Date(y, m - 1 + closeShift + dueShift, 1)
  return statementMonthISO(due.getFullYear(), due.getMonth())
}
